/**
 * Design Library — cover patterns (whole-document opening or standalone formats).
 * Pure design knowledge: no pixels, geometry, or editor concepts.
 */

import { definePattern } from './_define';

export const COVER_PATTERNS = [
  definePattern({
    id: 'editorial-cover', name: 'Editorial Cover', family: 'cover',
    purpose: 'A magazine-grade cover that sets the whole tone.',
    communicationGoal: 'Establish authority and desire in one frame.',
    visualGoal: 'One commanding image beneath a masthead.',
    contentGoal: 'Masthead, cover line, issue detail.',
    marketingGoal: 'Make the piece worth picking up.',
    idealDocuments: ['Brochure', 'Presentation', 'Poster'],
    idealIndustries: ['fashion', 'hotel', 'real-estate', 'travel', 'wedding'],
    idealAudiences: ['luxury-customers', 'professionals', 'women'],
    idealGenomes: ['luxury-fashion', 'editorial', 'luxury'],
    compatiblePatterns: ['magazine-spread', 'image-band', 'editorial-columns'],
    incompatiblePatterns: ['bento-grid'],
    compositionPhilosophy: 'Full-bleed image with a masthead that owns the top.',
    hierarchyPhilosophy: 'Masthead, then cover line, then everything else.',
    typicalSections: ['masthead', 'cover-line', 'image', 'issue-detail'],
    preferredComponents: ['hero', 'logo'],
    premiumCues: ['refined-type', 'confident-scale', 'crafted-detail'],
    thingsToAvoid: ['clutter', 'stock-cliches'],
    variationRules: ['masthead-scale', 'cover-line-count'],
    capabilities: ['image', 'fullbleed', 'image-led'],
    tags: ['cover', 'editorial'],
    expansionRules: { emphasis: 'masthead', imageRole: 'full-bleed' },
  }),
  definePattern({
    id: 'presentation-cover', name: 'Presentation Cover', family: 'cover',
    purpose: 'The opening slide that frames a deck.',
    communicationGoal: 'State the subject and the speaker with clarity.',
    visualGoal: 'Confident, brand-led, uncluttered.',
    contentGoal: 'Title, subtitle, presenter, date, brand mark.',
    marketingGoal: 'Set expectations of competence.',
    idealDocuments: ['Presentation'],
    idealIndustries: ['corporate', 'technology', 'finance', 'education', 'medical'],
    idealAudiences: ['professionals', 'students'],
    idealGenomes: ['corporate', 'modern', 'swiss', 'bento', 'future-ui'],
    compatiblePatterns: ['logo-strip', 'gradient-hero', 'split-hero'],
    compositionPhilosophy: 'Brand-led with a clear title zone.',
    hierarchyPhilosophy: 'Title dominates; credits recede.',
    typicalSections: ['title', 'subtitle', 'presenter', 'date', 'brand'],
    preferredComponents: ['hero', 'logo'],
    premiumCues: ['precision', 'generous-whitespace'],
    thingsToAvoid: ['default-fonts', 'clutter'],
    variationRules: ['brand-placement', 'title-scale'],
    capabilities: ['text-led', 'logo', 'image'],
    tags: ['cover', 'deck', 'corporate'],
    expansionRules: { emphasis: 'title', tone: 'confident' },
  }),
  definePattern({
    id: 'certificate-layout', name: 'Certificate Layout', family: 'cover',
    purpose: 'A formal award document with ceremony.',
    communicationGoal: 'Confer recognition that feels earned.',
    visualGoal: 'Symmetric, framed, dignified.',
    contentGoal: 'Title, recipient, reason, date, signature, seal.',
    marketingGoal: 'Create a keepsake worth displaying.',
    idealDocuments: ['Certificate', 'Gift Certificate'],
    idealIndustries: ['education', 'corporate', 'fitness', 'events'],
    idealAudiences: ['students', 'professionals', 'general-public'],
    idealGenomes: ['editorial', 'luxury', 'corporate', 'minimal'],
    compatiblePatterns: ['logo-strip'],
    incompatiblePatterns: ['collage-wall', 'instagram-story-hero'],
    compositionPhilosophy: 'Strict symmetry inside a deliberate frame.',
    hierarchyPhilosophy: 'The recipient’s name is the hero; everything else frames it.',
    typicalSections: ['title', 'recipient', 'reason', 'date', 'signature', 'seal'],
    preferredComponents: ['hero', 'logo', 'footer'],
    premiumCues: ['crafted-detail', 'refined-type', 'generous-whitespace'],
    thingsToAvoid: ['clip-art', 'busy-background'],
    variationRules: ['frame-weight', 'seal-placement'],
    capabilities: ['text-led', 'logo'],
    tags: ['certificate', 'formal', 'cover'],
    expansionRules: { emphasis: 'recipient', axis: 'center' },
  }),
  definePattern({
    id: 'business-card-layout', name: 'Business Card Layout', family: 'cover',
    purpose: 'An identity distilled to a pocket-sized surface.',
    communicationGoal: 'Say who you are and how to reach you, instantly.',
    visualGoal: 'Extreme restraint; every millimetre earns its place.',
    contentGoal: 'Name, role, channels, brand mark, optional QR.',
    marketingGoal: 'Leave a tangible, premium impression.',
    idealDocuments: ['Business Card'],
    idealIndustries: ['corporate', 'legal', 'real-estate', 'salon', 'photography'],
    idealAudiences: ['professionals'],
    idealGenomes: ['minimal', 'luxury', 'swiss', 'corporate', 'scandinavian'],
    compatiblePatterns: ['qr-block', 'logo-strip'],
    incompatiblePatterns: ['collage-wall', 'magazine-spread'],
    compositionPhilosophy: 'Two zones: identity and contact, on one alignment.',
    hierarchyPhilosophy: 'Name first, brand second, channels third.',
    typicalSections: ['name', 'role', 'contact', 'brand'],
    preferredComponents: ['logo', 'contact'],
    premiumCues: ['restraint', 'precision', 'crafted-detail'],
    thingsToAvoid: ['tiny-type', 'clutter'],
    variationRules: ['orientation', 'qr-or-not'],
    capabilities: ['contact', 'logo', 'qr', 'text-led'],
    tags: ['card', 'identity', 'cover'],
    expansionRules: { emphasis: 'name', density: 'compact' },
  }),
];
